import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, Copy, Download, Loader2, Send, Trash2, Upload } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { DEFAULT_SIGNAL_COLOR, DEFAULT_SIGNAL_EMOJI, SignalColorPicker, SignalEmojiPicker } from '../../components/signal/SignalEditorFields';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { AutoSaveStatus } from '../../components/ui/AutoSaveStatus';
import { useAutoSave } from '../../lib/useAutoSave';
import { FormSection } from '../../components/ui/FormSection';
import { TextField } from '../../components/ui/TextField';
import { Toggle } from '../../components/ui/Toggle';
import { Button } from '../../components/ui/Button';
import { IconButton } from '../../components/ui/IconButton';
import { Toast } from '../../components/ui/Toast';
import { Sheet } from '../../components/ui/Sheet';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { AvatarImagePicker } from '../../components/ui/AvatarImagePicker';
import { useAvatarImageUrl } from '../../components/ui/AvatarView';
import {
  fetchTemplate,
  parseSignalsCSV,
  importSignalsCSV,
  type CSVSignalMapping,
  type CSVFormatOptions,
} from '../../api/dataApi';
import { queryClient } from '../../lib/queryClient';
import { DELIMITER_OPTIONS, resolveDelimiterParam, rowsToCSV, downloadCSV, type DelimiterChoice } from '../../lib/csvFormat';
import { buildSignalSnippet } from '../../lib/signalIntegrationSnippet';
import { countMappingsTargetingSignal } from '../../lib/signalMappingUsage';
import { uid, type Channel, type Template, type TemplateSignal } from '../../types';

// A template's own icon is an emoji + colorHex, not an AvatarView-style
// SF-symbol icon (that's what the *signals* inside it use - see
// SignalEmojiPicker above) - so this reuses AvatarView's image-fetch-and-
// cache hook directly rather than going through the AvatarView component
// itself, which always renders a lucide icon as its fallback.
function TemplateIconPreview({ emoji, colorHex, imageKey, size }: { emoji: string; colorHex: string; imageKey: string; size: number }) {
  const imageUrl = useAvatarImageUrl(imageKey);
  return (
    <div
      style={{ width: size, height: size, borderRadius: size * 0.28, background: imageUrl ? undefined : `${colorHex}2e`, overflow: 'hidden' }}
      className="flex items-center justify-center shrink-0 text-3xl"
    >
      {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> : emoji}
    </div>
  );
}

const CSV_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'value', label: 'Value', required: false },
  { key: 'colorHex', label: 'Color', required: false },
  { key: 'emoji', label: 'Emoji', required: false },
  { key: 'promptForValue', label: 'Ask for value', required: false },
] as const;
type CSVFieldKey = (typeof CSV_FIELDS)[number]['key'];

const CSV_FIELD_KEYWORDS: Record<CSVFieldKey, string[]> = {
  name: ['name', 'signal', 'title'],
  value: ['value', 'amount'],
  colorHex: ['color', 'colour', 'hex'],
  emoji: ['emoji', 'icon'],
  promptForValue: ['promptforvalue', 'askforvalue', 'prompt', 'ask'],
};

// Phase 3 of docs/SIGNAL_TEMPLATES_AS_MAPPING_SCHEMA_PLAN.md's "Copy
// integration snippet" - the actual Excel/VBA answer: paste this signal's
// own number into a script and POST straight to /signal, no Mapping/
// `rules` authoring at all. Only shown for a signal that's actually been
// assigned a number (see TemplateSignal.number's own comment - absent
// until the template has been saved at least once).
function SignalSnippetSheet({
  open,
  onClose,
  signalName,
  number,
  channels,
}: {
  open: boolean;
  onClose: () => void;
  signalName: string;
  number: string;
  channels: Channel[];
}) {
  const [channelId, setChannelId] = useState(channels[0]?.nickname ?? '');
  const [copied, setCopied] = useState<'json' | 'curl' | 'vba' | null>(null);
  const snippet = buildSignalSnippet({ number, channelId });

  const copy = async (which: 'json' | 'curl' | 'vba', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied((c) => (c === which ? null : c)), 1500);
    } catch {
      // Clipboard access denied/unavailable - the text is still on screen
      // to select and copy by hand.
    }
  };

  const Block = ({ which, label, text }: { which: 'json' | 'curl' | 'vba'; label: string; text: string }) => (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-black/60 dark:text-white/60">{label}</span>
        <Button variant="secondary" onClick={() => copy(which, text)}>
          {copied === which ? <Check size={14} /> : <Copy size={14} />}
          {copied === which ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className="rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2.5 text-[12px] font-mono whitespace-pre-wrap break-all select-all overflow-x-auto">
        {text}
      </pre>
    </div>
  );

  return (
    <Sheet open={open} onClose={onClose} title={`Integration snippet — ${signalName || 'signal'} #${number}`}>
      <div className="flex flex-col gap-4">
        <p className="text-xs text-black/45 dark:text-white/45">
          Sends this exact preset (type/value come from signal #{number} itself) into a channel - no Mapping needed.
          Replace <code>&lt;your-token&gt;</code> with a real session/API token before using.
        </p>
        <Select label="Channel" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
          {channels.length === 0 && <option value="">@your-channel</option>}
          {channels.map((c) => (
            <option key={c.nickname} value={c.nickname}>
              {c.title} ({c.nickname})
            </option>
          ))}
        </Select>
        <Block which="json" label="JSON body" text={snippet.json} />
        <Block which="curl" label="curl" text={snippet.curl} />
        <Block which="vba" label="Excel / VBA" text={snippet.vba} />
      </div>
    </Sheet>
  );
}

// Pick a CSV file, say whether its first row is a header, map each target
// field to a source column (best-guessed from header text when present,
// always editable), preview the result, then hand back every row that
// resolved to at least a name. The actual CSV parsing and the final
// mapping-to-signals conversion both happen server-side
// (parseSignalsCSV/importSignalsCSV in dataApi.ts) - see
// backend/modules/template/transitions/template.go's ParseCSVRows/
// ImportSignalsCSV - so real quoted-field CSV handling and the
// name/value/color/emoji defaulting logic live in exactly one place,
// shared with the iOS app's equivalent flow, instead of being
// reimplemented per client. Only the interactive preview below (re-slicing
// rows the backend already parsed and returned) stays local, purely for
// instant feedback as someone adjusts a column mapping - the actual
// Import always re-derives the final result from the backend, not this
// preview.
function CSVSignalImportSheet({
  open,
  onClose,
  onImport,
}: {
  open: boolean;
  onClose: () => void;
  onImport: (signals: TemplateSignal[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [csvText, setCsvText] = useState('');
  const [rows, setRows] = useState<string[][]>([]);
  const [hasHeaderRow, setHasHeaderRow] = useState(true);
  const [mapping, setMapping] = useState<Partial<Record<CSVFieldKey, number>>>({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  // Auto-detect (the backend's own delimiter sniffing) is the default -
  // these two only matter once someone overrides them because
  // auto-detection guessed wrong for their file.
  const [delimiterChoice, setDelimiterChoice] = useState<DelimiterChoice>('auto');
  const [customDelimiter, setCustomDelimiter] = useState('');
  const [quoted, setQuoted] = useState(true);

  const currentFormat = (): CSVFormatOptions => ({
    delimiter: resolveDelimiterParam(delimiterChoice, customDelimiter),
    quoted,
  });

  const headerRow = hasHeaderRow ? rows[0] : undefined;
  const dataRows = hasHeaderRow ? rows.slice(1) : rows;
  const columnCount = rows.reduce((max, r) => Math.max(max, r.length), 0);

  const columnLabel = (i: number) => headerRow?.[i]?.trim() || `Column ${i + 1}`;

  const fieldValue = (row: string[], key: CSVFieldKey): string | undefined => {
    const idx = mapping[key];
    if (idx === undefined || idx >= row.length) return undefined;
    const v = row[idx].trim();
    return v === '' ? undefined : v;
  };

  // Local re-derivation of what the backend's ImportSignalsCSV will
  // produce, purely for the live preview list - mirrors its
  // defaulting/# prefixing so the preview doesn't visibly disagree with
  // what Import is about to fetch for real.
  const previewSignal = (row: string[]): TemplateSignal | null => {
    const name = fieldValue(row, 'name');
    if (!name) return null;
    let colorHex = fieldValue(row, 'colorHex') ?? DEFAULT_SIGNAL_COLOR;
    if (!colorHex.startsWith('#')) colorHex = `#${colorHex}`;
    const promptRaw = fieldValue(row, 'promptForValue')?.toLowerCase();
    return {
      id: uid(),
      name,
      value: fieldValue(row, 'value'),
      promptForValue: ['true', 'yes', 'y', '1'].includes(promptRaw ?? ''),
      colorHex,
      emoji: fieldValue(row, 'emoji') ?? DEFAULT_SIGNAL_EMOJI,
    };
  };

  const previewSignals = dataRows.map(previewSignal).filter((s): s is TemplateSignal => s !== null);

  // Best-effort guess at which column is which, based on header text -
  // still fully editable via the selects below, this just saves setting
  // every mapping by hand for a normally-named export.
  const autoMapColumns = (header: string[] | undefined) => {
    const next: Partial<Record<CSVFieldKey, number>> = {};
    if (header) {
      for (const field of CSV_FIELDS) {
        const idx = header.findIndex((h) => {
          const normalized = h.toLowerCase().replace(/\s+/g, '');
          return CSV_FIELD_KEYWORDS[field.key].some((k) => normalized.includes(k));
        });
        if (idx !== -1) next[field.key] = idx;
      }
    }
    setMapping(next);
  };

  // Shared by the initial file pick and by re-parsing after someone
  // overrides the delimiter/quoted controls below - takes `text`/
  // `format` explicitly rather than reading state, since a just-called
  // setCsvText/setDelimiterChoice hasn't landed yet when this runs.
  const parseAndSetRows = async (text: string, format: CSVFormatOptions) => {
    setLoading(true);
    setError('');
    try {
      // Routes through queryClient.query - a one-shot, user-triggered
      // action (pick a file, or change the delimiter/quoted controls),
      // so the real value here is dedup if handleFormatChange fires twice
      // in quick succession (e.g. two format controls both changing in
      // the same tick) rather than caching across separate user intents.
      const parsedRows = await queryClient.query({
        queryKey: ['csvParseSignals', text, format.delimiter, format.quoted],
        queryFn: () => parseSignalsCSV(text, format),
      });
      if (parsedRows.length === 0) {
        setError("That file doesn't contain any rows.");
        setRows([]);
        return;
      }
      setRows(parsedRows);
      autoMapColumns(hasHeaderRow ? parsedRows[0] : undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to read that file.');
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (file: File) => {
    const text = await file.text();
    setCsvText(text);
    await parseAndSetRows(text, currentFormat());
  };

  // Re-parses the already-loaded file against a corrected delimiter/
  // quoted choice - a no-op before any file is picked.
  const handleFormatChange = (format: CSVFormatOptions) => {
    if (!csvText) return;
    void parseAndSetRows(csvText, format);
  };

  const columnParam = (key: CSVFieldKey) => (mapping[key] !== undefined ? String(mapping[key]) : '');

  const commitImport = async () => {
    setImporting(true);
    setError('');
    try {
      const mappingParam: CSVSignalMapping = {
        name: columnParam('name'),
        value: columnParam('value'),
        colorHex: columnParam('colorHex'),
        emoji: columnParam('emoji'),
        promptForValue: columnParam('promptForValue'),
      };
      // Write, not a read - retry: 0, same rationale as every other
      // mutation routed through queryClient.query this session (see
      // ChannelDetailsScreen.tsx's toggleLike comment).
      const format = currentFormat();
      const result = await queryClient.query({
        queryKey: ['csvImportSignals', csvText, hasHeaderRow, mappingParam, format.delimiter, format.quoted],
        queryFn: () => importSignalsCSV(csvText, hasHeaderRow, mappingParam, format),
        retry: 0,
        staleTime: 0,
      });
      onImport(result.signals);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to import that CSV file.');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setCsvText('');
    setRows([]);
    setMapping({});
    setError('');
    setDelimiterChoice('auto');
    setCustomDelimiter('');
    setQuoted(true);
    onClose();
  };

  return (
    <Sheet open={open} onClose={handleClose} title="Import from CSV">
      <div className="flex flex-col gap-4">
        {rows.length === 0 ? (
          <>
            <Button variant="bordered" onClick={() => inputRef.current?.click()} loading={loading}>
              Choose CSV file
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
                if (inputRef.current) inputRef.current.value = '';
              }}
            />
          </>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm text-black/70 dark:text-white/70">
              <input
                type="checkbox"
                checked={hasHeaderRow}
                onChange={(e) => {
                  setHasHeaderRow(e.target.checked);
                  autoMapColumns(e.target.checked ? rows[0] : undefined);
                }}
              />
              First row is a header
            </label>
            <Select
              label="Delimiter"
              value={delimiterChoice}
              onChange={(e) => {
                const next = e.target.value as DelimiterChoice;
                setDelimiterChoice(next);
                if (next !== 'custom') handleFormatChange({ delimiter: resolveDelimiterParam(next, customDelimiter), quoted });
              }}
            >
              {DELIMITER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            {delimiterChoice === 'custom' && (
              <TextField
                label="Custom delimiter character"
                placeholder="e.g. :"
                value={customDelimiter}
                onChange={(e) => {
                  const v = e.target.value;
                  setCustomDelimiter(v);
                  if (v) handleFormatChange({ delimiter: v, quoted });
                }}
              />
            )}
            <label className="flex items-center gap-2 text-sm text-black/70 dark:text-white/70">
              <input
                type="checkbox"
                checked={quoted}
                onChange={(e) => {
                  const v = e.target.checked;
                  setQuoted(v);
                  handleFormatChange({ delimiter: resolveDelimiterParam(delimiterChoice, customDelimiter), quoted: v });
                }}
              />
              Quoted fields (RFC 4180)
            </label>
            <div className="flex flex-col gap-3">
              {CSV_FIELDS.map((field) => (
                <Select
                  key={field.key}
                  label={field.label + (field.required ? ' (required)' : '')}
                  value={mapping[field.key] ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMapping((m) => {
                      const next = { ...m };
                      if (v === '') delete next[field.key];
                      else next[field.key] = Number(v);
                      return next;
                    });
                  }}
                >
                  <option value="">None</option>
                  {Array.from({ length: columnCount }, (_, i) => (
                    <option key={i} value={i}>
                      {columnLabel(i)}
                    </option>
                  ))}
                </Select>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">
                Preview - {previewSignals.length} signal{previewSignals.length === 1 ? '' : 's'} will be imported
              </span>
              <div className="flex flex-col gap-2">
                {previewSignals.slice(0, 5).map((s) => (
                  <div key={s.id} className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-full text-sm flex items-center justify-center shrink-0 border-2"
                      style={{ background: `${s.colorHex}1f`, borderColor: s.colorHex }}
                    >
                      {s.emoji}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm">{s.name}</span>
                      {s.value && <span className="text-xs text-black/50 dark:text-white/50">{s.value}</span>}
                    </div>
                  </div>
                ))}
              </div>
              {previewSignals.length > 5 && (
                <span className="text-xs text-black/50 dark:text-white/50">+ {previewSignals.length - 5} more</span>
              )}
              {dataRows.length > previewSignals.length && (
                <span className="text-xs text-black/50 dark:text-white/50">
                  {dataRows.length - previewSignals.length} row(s) will be skipped - no Name column mapped or empty in that row.
                </span>
              )}
            </div>
            <Button onClick={commitImport} disabled={previewSignals.length === 0} loading={importing}>
              Import
            </Button>
          </>
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    </Sheet>
  );
}

function blankDraft(): Template {
  return {
    id: '',
    title: 'New Schema',
    nickname: '@template',
    isPublic: false,
    colorHex: '#10B981',
    emoji: '✨',
    avatarImageKey: '',
    signals: [{ id: uid(), name: 'Signal', colorHex: '#10B981', emoji: '⚡️' }],
    likes: 0,
    liked: false,
    usages: 0,
    ownerId: '',
  };
}

export function TemplateManageScreen() {
  const { templateId } = useParams();
  const isNew = !templateId;
  const navigate = useNavigate();
  const { templates, addTemplate, updateTemplate, renameTemplate, checkTemplateNicknameAvailable, isTemplateOwner, profile, channels, mappings } = useStore();
  const [snippetSignal, setSnippetSignal] = useState<TemplateSignal | null>(null);

  const [draft, setDraft] = useState<Template>(blankDraft);
  // Edit is owner-only - see ChannelManageScreen's identical `access` gate
  // for the full rationale. The old guard only redirected a non-owner when
  // the template happened to be cached; when it wasn't (the case a
  // non-owner poking at /templates/@x/edit hits) the blank-draft form
  // rendered anyway. update.wsl rejects a non-owner 403 regardless.
  const [access, setAccess] = useState<'checking' | 'granted' | 'denied'>(isNew ? 'granted' : 'checking');
  // Reactive mirror of draftLoadedRef - see ChannelManageScreen.
  const [draftLoaded, setDraftLoaded] = useState(isNew);
  const [newSignalName, setNewSignalName] = useState('');
  const [newSignalValue, setNewSignalValue] = useState('');
  const [newSignalPromptForValue, setNewSignalPromptForValue] = useState(false);
  const [newSignalColor, setNewSignalColor] = useState(DEFAULT_SIGNAL_COLOR);
  const [newSignalEmoji, setNewSignalEmoji] = useState(DEFAULT_SIGNAL_EMOJI);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  // Rename is a dedicated migration, not an auto-saved field - see
  // ChannelManageScreen's identical nicknameDraft comment for why.
  const [nicknameDraft, setNicknameDraft] = useState('');
  const [nicknameStatus, setNicknameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [renamingTemplate, setRenamingTemplate] = useState(false);

  // Resolve access + load the draft - see ChannelManageScreen's identical
  // effect. A template the caller doesn't have cached is fetched fresh so
  // ownership can still be checked; anything that doesn't come back owned
  // by the caller is denied and bounced to the read-only view.
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (isNew || !templateId) return;
    if (!profile.nickname) return; // isTemplateOwner keys off the nickname
    let cancelled = false;

    const decide = (t: Template | null | undefined) => {
      if (cancelled) return;
      if (t && isTemplateOwner(t)) {
        setAccess('granted');
        if (!draftLoadedRef.current) {
          setDraft(t);
          setNicknameDraft(t.nickname);
          draftLoadedRef.current = true;
          setDraftLoaded(true);
        }
      } else {
        setAccess('denied');
      }
    };

    const cached = templates.find((t) => t.id === templateId);
    if (cached) {
      decide(cached);
      return;
    }
    fetchTemplate(templateId).then(decide);
    return () => {
      cancelled = true;
    };
  }, [templateId, isNew, templates, profile.nickname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (access === 'denied' && templateId) {
      navigate(`/templates/${templateId}`, { replace: true });
    }
  }, [access, templateId, navigate]);

  const addSignal = () => {
    if (!newSignalName.trim()) return;
    setDraft((d) => ({
      ...d,
      signals: [
        ...d.signals,
        {
          id: uid(),
          name: newSignalName.trim(),
          value: newSignalValue.trim() || undefined,
          promptForValue: newSignalPromptForValue,
          colorHex: newSignalColor,
          emoji: newSignalEmoji,
        },
      ],
    }));
    setNewSignalName('');
    setNewSignalValue('');
    setNewSignalPromptForValue(false);
  };

  const updateSignal = (id: string, patch: Partial<TemplateSignal>) => {
    setDraft((d) => ({
      ...d,
      signals: d.signals.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    }));
  };

  const removeSignal = (id: string) => {
    setDraft((d) => ({ ...d, signals: d.signals.filter((s) => s.id !== id) }));
  };

  // Client-side only, no backend round trip - the columns mirror
  // CSV_FIELDS exactly, so a file exported here re-imports cleanly
  // through this same screen's "Import from CSV" flow. Gated by
  // isTemplateOwner the same way the Edit screen itself already is (see
  // that check's own comment on why this is a client-side convenience,
  // not a real security boundary - GET /template/{nickname} has no
  // ownership check server-side either, so a non-owner already sees this
  // exact signals data in-app; this only controls who's *offered* a
  // one-click download of it).
  const exportSignalsCSV = () => {
    const header = ['Name', 'Value', 'Color', 'Emoji', 'Ask for value'];
    const rows = draft.signals.map((s) => [s.name, s.value ?? '', s.colorHex, s.emoji, s.promptForValue ? 'true' : 'false']);
    downloadCSV(`${draft.nickname.replace(/^@/, '') || 'template'}-signals.csv`, rowsToCSV([header, ...rows]));
  };

  // Create path only - an existing template auto-saves (below).
  const save = async () => {
    setSaving(true);
    try {
      await addTemplate(draft);
      navigate('/templates');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save schema.');
    } finally {
      setSaving(false);
    }
  };

  // Edit path: debounced auto-save.
  const auto = useAutoSave(draft, updateTemplate, {
    enabled: !isNew && access === 'granted' && draftLoaded,
  });

  useEffect(() => {
    if (isNew || !draftLoaded) return;
    const next = nicknameDraft.trim();
    if (!next || next === draft.nickname) {
      setNicknameStatus('idle');
      return;
    }
    setNicknameStatus('checking');
    const timer = setTimeout(async () => {
      const available = await checkTemplateNicknameAvailable(next);
      setNicknameStatus(available ? 'available' : 'taken');
    }, 400);
    return () => clearTimeout(timer);
  }, [nicknameDraft, draft.nickname, isNew, draftLoaded, checkTemplateNicknameAvailable]);

  const confirmRename = async () => {
    setRenamingTemplate(true);
    setError('');
    try {
      const renamed = await renameTemplate(draft.id, nicknameDraft.trim());
      // See ChannelManageScreen.tsx's identical confirmRename comment -
      // the navigate() below doesn't remount this component (React Router
      // keeps the same instance across a param-only URL change), so local
      // state has to be set directly or nicknameDraft/draft.nickname stay
      // mismatched and the availability check keeps firing forever.
      setDraft(renamed);
      setNicknameDraft(renamed.nickname);
      setNicknameStatus('idle');
      navigate(`/templates/${renamed.id}/edit`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to rename schema.');
    } finally {
      setRenamingTemplate(false);
    }
  };
  useEffect(() => {
    if (auto.error) setError(auto.error.message);
  }, [auto.error]);

  // Don't render the editable form until we've confirmed the caller owns
  // this template (see `access` above).
  if (access !== 'granted') {
    return (
      <div className="flex flex-col gap-5">
        <ScreenHeader title="Edit schema" />
        {access === 'checking' && (
          <div className="flex items-center gap-2 px-1 text-sm text-black/50 dark:text-white/50">
            <Loader2 size={16} className="animate-spin" />
            Loading…
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5" onBlur={isNew ? undefined : auto.flush}>
      <ScreenHeader
        title={isNew ? 'Create schema' : 'Edit schema'}
        action={
          isNew ? (
            <IconButton
              icon={saving ? Loader2 : Check}
              onClick={save}
              disabled={saving}
              aria-label="Save"
              className={saving ? 'animate-spin' : ''}
            />
          ) : (
            <AutoSaveStatus status={auto.status} onRetry={auto.retry} />
          )
        }
      />

      <FormSection title="Icon">
        <div className="flex items-center gap-4">
          <div className="relative">
            <TemplateIconPreview emoji={draft.emoji} colorHex={draft.colorHex} imageKey={draft.avatarImageKey} size={72} />
            <div className="absolute -bottom-1 -right-1">
              <AvatarImagePicker
                size={72}
                onUploaded={(key) => setDraft((d) => ({ ...d, avatarImageKey: key }))}
                onError={setError}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <SignalEmojiPicker
                emoji={draft.emoji}
                colorHex={draft.colorHex}
                onEmojiChange={(emoji) => setDraft({ ...draft, emoji })}
              />
              <span className="text-sm text-black/50 dark:text-white/50">
                Pick an emoji or type up to 2 characters. An uploaded image takes priority over this.
              </span>
            </div>
            {draft.avatarImageKey && (
              <button
                type="button"
                onClick={() => setDraft({ ...draft, avatarImageKey: '' })}
                className="self-start text-sm text-black/50 dark:text-white/50 underline underline-offset-2"
              >
                Remove uploaded image
              </button>
            )}
          </div>
        </div>
      </FormSection>

      <FormSection title="Schema">
        <TextField label="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        {isNew ? (
          <TextField label="Nickname" value={draft.nickname} onChange={(e) => setDraft({ ...draft, nickname: e.target.value })} />
        ) : (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <TextField
                  label="Nickname"
                  value={nicknameDraft}
                  onChange={(e) => setNicknameDraft(e.target.value)}
                  error={nicknameStatus === 'taken' ? 'That nickname is already taken.' : undefined}
                />
              </div>
              {nicknameDraft.trim() && nicknameDraft.trim() !== draft.nickname && (
                <Button
                  variant="bordered"
                  onClick={confirmRename}
                  disabled={nicknameStatus !== 'available' || renamingTemplate}
                  loading={renamingTemplate}
                >
                  Rename
                </Button>
              )}
            </div>
            {nicknameStatus === 'checking' && (
              <span className="text-xs text-black/40 dark:text-white/40">Checking availability…</span>
            )}
            {nicknameStatus === 'available' && (
              <span className="text-xs text-emerald-600 dark:text-emerald-400">Available — click Rename to confirm.</span>
            )}
          </div>
        )}
        <Toggle label="Public" checked={draft.isPublic} onChange={(v) => setDraft({ ...draft, isPublic: v })} />
      </FormSection>

      <FormSection title="Signals">
        <div className="flex gap-2">
          <Button variant="bordered" onClick={() => setCsvImportOpen(true)}>
            <Upload size={16} />
            Import from CSV
          </Button>
          {isTemplateOwner(draft) && draft.signals.length > 0 && (
            <Button variant="bordered" onClick={exportSignalsCSV}>
              <Download size={16} />
              Export to CSV
            </Button>
          )}
        </div>
        <div className="flex flex-col divide-y divide-black/5 dark:divide-white/10">
          {draft.signals.map((signal) => (
            <div key={signal.id} className="flex flex-col gap-2 py-3">
              <div className="flex items-center gap-2">
                <SignalEmojiPicker
                  emoji={signal.emoji}
                  colorHex={signal.colorHex}
                  onEmojiChange={(emoji) => updateSignal(signal.id, { emoji })}
                />
                <TextField
                  aria-label="Signal name"
                  value={signal.name}
                  onChange={(e) => updateSignal(signal.id, { name: e.target.value })}
                />
                <TextField
                  aria-label="Signal value"
                  placeholder="Value (optional)"
                  value={signal.value ?? ''}
                  onChange={(e) => updateSignal(signal.id, { value: e.target.value || undefined })}
                />
                {signal.number && (
                  <IconButton
                    icon={Send}
                    aria-label="Copy integration snippet"
                    onClick={() => setSnippetSignal(signal)}
                    className="shrink-0"
                  />
                )}
                <IconButton
                  icon={Trash2}
                  aria-label="Remove signal"
                  onClick={() => removeSignal(signal.id)}
                  className="shrink-0"
                />
              </div>
              <div className="flex items-center gap-2">
                <span title="This is the number an integration's `signalRef` rule targets to point at this exact signal.">
                  <Badge>{signal.number ? `#${signal.number}` : 'no number yet - save to assign one'}</Badge>
                </span>
              </div>
              <label className="flex items-center gap-2 text-sm text-black/70 dark:text-white/70">
                <input
                  type="checkbox"
                  checked={signal.promptForValue ?? false}
                  onChange={(e) => updateSignal(signal.id, { promptForValue: e.target.checked })}
                />
                Ask for a value when sending
              </label>
              {signal.number && (() => {
                const used = countMappingsTargetingSignal(mappings, signal.number!);
                return used > 0 ? (
                  <span title="Counts only your own mappings - another account's mapping could target this same signal without showing up here.">
                    <Badge>
                      Used by {used} of your mapping{used === 1 ? '' : 's'}
                    </Badge>
                  </span>
                ) : null;
              })()}
              <SignalColorPicker
                colorHex={signal.colorHex}
                onColorChange={(color) => updateSignal(signal.id, { colorHex: color })}
              />
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2 pt-2">
          <div className="flex gap-2">
            <SignalEmojiPicker emoji={newSignalEmoji} colorHex={newSignalColor} onEmojiChange={setNewSignalEmoji} />
            <TextField
              placeholder="New signal"
              value={newSignalName}
              onChange={(e) => setNewSignalName(e.target.value)}
            />
            <TextField
              placeholder="Value (optional)"
              value={newSignalValue}
              onChange={(e) => setNewSignalValue(e.target.value)}
            />
            <Button variant="bordered" onClick={addSignal}>
              Add
            </Button>
          </div>
          <label className="flex items-center gap-2 text-sm text-black/70 dark:text-white/70">
            <input
              type="checkbox"
              checked={newSignalPromptForValue}
              onChange={(e) => setNewSignalPromptForValue(e.target.checked)}
            />
            Ask for a value when sending
          </label>
          <SignalColorPicker colorHex={newSignalColor} onColorChange={setNewSignalColor} />
        </div>
      </FormSection>

      <CSVSignalImportSheet
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        onImport={(imported) => {
          setDraft((d) => ({ ...d, signals: [...d.signals, ...imported] }));
          setCsvImportOpen(false);
        }}
      />

      {snippetSignal && (
        <SignalSnippetSheet
          open={!!snippetSignal}
          onClose={() => setSnippetSignal(null)}
          signalName={snippetSignal.name}
          number={snippetSignal.number!}
          channels={channels}
        />
      )}

      {error && <Toast message={error} onDismiss={() => setError('')} />}
    </div>
  );
}
